import type { PrioridadTarea, TareaPublic } from '@app/shared';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CalendarDays, Check, GripVertical, Pencil, Trash2 } from 'lucide-react';

import { useI18n } from '../../../i18n/I18nProvider';
import { cn } from '../../../lib/cn';
import { diaIsoDeSemana, diaMes } from '../lib/fechas';

// ciclo-tareas, R5: tarjeta horizontal de ancho completo. El color de prioridad
// vive en el borde izquierdo y en la etiqueta (pastel de relleno) — la tarjeta
// entera se queda neutra para no competir con el resto de la UI. Tokens
// semánticos ya existentes (danger/warning/success), sin colores nuevos.
const PRIORIDAD_BORDE: Record<PrioridadTarea, string> = {
  ALTA: 'border-l-danger',
  MEDIA: 'border-l-warning',
  BAJA: 'border-l-success',
};
const PRIORIDAD_BADGE: Record<PrioridadTarea, string> = {
  ALTA: 'bg-danger/15 text-danger',
  MEDIA: 'bg-warning/15 text-warning',
  BAJA: 'bg-success/15 text-success',
};

interface Props {
  tarea: TareaPublic;
  // true = semana GUARDADA (congelada): se puede tildar, no se puede editar la
  // estructura. false = PLANIFICADA: al revés (circuito de Javier, 28/09/2026).
  semanaGuardada: boolean;
  onToggleCompletada: () => void;
  onToggleActiva: () => void;
  onEditar: () => void;
  // D-044: eliminar desde la tarjeta (la página pide confirmación).
  onEliminar: () => void;
}

export function TarjetaTarea({
  tarea,
  semanaGuardada,
  onToggleCompletada,
  onToggleActiva,
  onEditar,
  onEliminar,
}: Props): JSX.Element {
  const es = useI18n();
  const t = es.tareas;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tarea.id,
    disabled: semanaGuardada,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group flex items-center gap-3 rounded-xl border-l-4 bg-surface p-4 shadow-card transition-shadow hover:shadow-card-hover',
        PRIORIDAD_BORDE[tarea.prioridad],
        isDragging && 'opacity-50 shadow-card-hover',
        !tarea.activa && 'opacity-60',
      )}
    >
      <button
        type="button"
        disabled={semanaGuardada}
        {...attributes}
        {...listeners}
        aria-label="Arrastrar para reordenar"
        className="cursor-grab touch-none text-text-faint hover:text-text-strong active:cursor-grabbing disabled:cursor-default disabled:opacity-30 disabled:hover:text-text-faint"
      >
        <GripVertical size={18} />
      </button>

      <button
        type="button"
        onClick={onToggleCompletada}
        disabled={!semanaGuardada}
        aria-label={tarea.completada ? t.descompletar : t.completar}
        aria-pressed={tarea.completada}
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 transition-colors disabled:cursor-not-allowed disabled:opacity-40',
          tarea.completada
            ? 'border-marca-primario bg-marca-primario text-white'
            : 'border-marca-neutro text-transparent hover:border-marca-primario',
        )}
      >
        <Check size={16} />
      </button>

      <button
        type="button"
        onClick={onEditar}
        disabled={semanaGuardada}
        className="min-w-0 flex-1 text-left disabled:cursor-default"
      >
        <span
          className={cn(
            'block truncate font-medium text-text-strong',
            tarea.completada && 'text-text-faint line-through',
          )}
        >
          {tarea.titulo}
        </span>
        {tarea.descripcion && (
          <span className="block truncate text-sm text-text-subtle">{tarea.descripcion}</span>
        )}
      </button>

      {/* ciclo-tareas-deadline, R5: el día en que vence ("jue 22/10"). */}
      <span
        data-testid="vencimiento"
        className={cn(
          'flex shrink-0 items-center gap-1 text-xs',
          tarea.venceEl ? 'text-text-subtle' : 'font-medium text-warning',
        )}
      >
        <CalendarDays size={14} />
        {tarea.venceEl
          ? `${t.dias.split('|')[diaIsoDeSemana(tarea.venceEl)] ?? ''} ${diaMes(tarea.venceEl)}`
          : t.sinFecha}
      </span>

      <span
        className={cn(
          'hidden shrink-0 rounded-full px-2 py-0.5 text-xs font-medium uppercase tracking-wide sm:block',
          PRIORIDAD_BADGE[tarea.prioridad],
        )}
      >
        {t.prioridad[tarea.prioridad]}
      </span>

      {!tarea.activa && (
        <span className="hidden shrink-0 rounded-full bg-surface-muted px-2 py-0.5 text-xs text-text-subtle sm:block">
          {t.desactivada}
        </span>
      )}

      <button
        type="button"
        onClick={onToggleActiva}
        disabled={semanaGuardada}
        className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-text-subtle hover:bg-surface-muted hover:text-text-strong disabled:pointer-events-none disabled:opacity-30"
      >
        {tarea.activa ? t.desactivar : t.activar}
      </button>

      <button
        type="button"
        onClick={onEditar}
        disabled={semanaGuardada}
        aria-label={t.dialogo.tituloEditar}
        className="shrink-0 text-text-faint hover:text-text-strong disabled:pointer-events-none disabled:opacity-30"
      >
        <Pencil size={16} />
      </button>

      {/* Mismo candado que editar: con la semana en curso la lista está congelada. */}
      <button
        type="button"
        onClick={onEliminar}
        disabled={semanaGuardada}
        aria-label={t.dialogo.eliminar}
        className="shrink-0 text-text-faint hover:text-danger disabled:pointer-events-none disabled:opacity-30"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}
